/**
 * Whether an add-on (or a form toggle) collects one set of answers for the
 * whole purchase / submission, or one set per selected business.
 */
export const BusinessScope = {
  Shared: "SHARED",
  PerBusiness: "PER_BUSINESS",
} as const;
export type BusinessScopeValue = (typeof BusinessScope)[keyof typeof BusinessScope];

/**
 * How a per-business add-on is priced: once for the purchase regardless of how
 * many businesses it covers, or once per covered business.
 */
export const AddOnPricingScope = {
  PerPurchase: "PER_PURCHASE",
  PerBusiness: "PER_BUSINESS",
} as const;
export type AddOnPricingScopeValue =
  (typeof AddOnPricingScope)[keyof typeof AddOnPricingScope];

export type BusinessScoped<T = object> = {
  operatorId: string;
  businessName?: string | null;
} & T;

export const isPerBusinessScope = (scope?: string | null): boolean =>
  scope === BusinessScope.PerBusiness;

/**
 * Units billed for a per-business add-on covering `businessCount` businesses.
 */
export const perBusinessQuantity = (
  pricingScope: string | null | undefined,
  businessCount: number,
): number =>
  pricingScope === AddOnPricingScope.PerBusiness ? Math.max(1, businessCount) : 1;

type ChoiceAnswer = {
  fieldId: string;
  response?: readonly (string | null | undefined)[] | null;
};

/**
 * Choices picked across the fields that charge per choice, e.g. three
 * categories ticked on a "Categories" checkbox group.
 */
export const countChargedChoices = (
  chargedFieldIds: readonly string[],
  answers: readonly ChoiceAnswer[] | null | undefined,
): number =>
  chargedFieldIds.reduce(
    (count, fieldId) =>
      count +
      (answers?.find(answer => String(answer.fieldId) === fieldId)?.response ?? [])
        .filter(Boolean).length,
    0,
  );

/**
 * Keep only the entries whose business is still selected, in selection order.
 * Entries without an `operatorId` are not business-scoped and are kept as-is,
 * ahead of the scoped ones.
 */
export function alignToSelection<T extends { operatorId?: string | null }>(
  selectedOperatorIds: readonly string[],
  scoped: readonly T[] | null | undefined,
): T[] {
  const entries = scoped ?? [];
  const unscoped = entries.filter(entry => !entry.operatorId);
  const byOperator = new Map<string, T[]>();
  for (const entry of entries) {
    if (!entry.operatorId) continue;
    const key = String(entry.operatorId);
    byOperator.set(key, [...(byOperator.get(key) ?? []), entry]);
  }
  return [
    ...unscoped,
    ...selectedOperatorIds.flatMap(id => byOperator.get(String(id)) ?? []),
  ];
}

/** 1-based slot of a business in the purchase / submission. */
export const businessSlotKey = (slot: number): string => `business.${slot}`;
export const businessSlotTitle = (slot: number): string => `Business ${slot}`;

/** Column key for an answer collected per business, `slot` 1-based. */
export const scopedColumnKey = (base: string, slot: number): string =>
  `${base}.b${slot}`;
export const scopedColumnTitle = (base: string, slot: number): string =>
  `${base} (Business ${slot})`;
